import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { analyticsApi, ApiRequestError, type CoverageDto } from "../../api/api";
import {
  AnalyticsFilterBar,
  lineTitle,
  type AnalyticsFiltersState,
} from "../../components/ui/AnalyticsFilterBar";
import { PageHeader } from "../../components/ui/PageHeader";

export function TeamCoveragePage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AnalyticsFiltersState>({
    year: new Date().getFullYear(),
  });
  const [data, setData] = useState<CoverageDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void analyticsApi
      .coverage(filters)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          setError(err instanceof ApiRequestError ? err.message : t("analytics_load_failed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, t]);

  return (
    <div className="sm-page">
      <PageHeader title={t("team_coverage")} />
      <AnalyticsFilterBar filters={filters} onChange={setFilters} />
      {error ? <p className="sm-error">{error}</p> : null}
      {loading ? (
        <p className="sm-muted">{t("loading")}</p>
      ) : (
        <div className="sm-card sm-card-pad">
          <p className="sm-muted" style={{ marginTop: 0 }}>
            {t("target_threshold")}: <strong>{data?.target}</strong>
          </p>
          <div className="sm-coverage-list">
            {(data?.coverage ?? []).map((c) => {
              const { title, subtitle } = lineTitle(c.line);
              const tone = c.isCritical ? "critical" : c.isSpof ? "spof" : "ok";
              return (
                <div key={c.line.id} className={`sm-coverage-row sm-coverage-${tone}`}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{title}</div>
                    <div className="sm-muted" style={{ fontSize: 11 }}>
                      {subtitle} — {c.line.domain}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>
                      {c.aboveTarget}/{c.total}
                    </div>
                    <div className="sm-muted" style={{ fontSize: 11 }}>
                      {t("above_target")}
                    </div>
                  </div>
                  {c.isCritical ? (
                    <span className="sm-badge-pill sm-badge-red">{t("critical")}</span>
                  ) : null}
                  {c.isSpof && !c.isCritical ? (
                    <span className="sm-badge-pill sm-badge-yellow">{t("spof_risk")}</span>
                  ) : null}
                </div>
              );
            })}
            {data && data.coverage.length === 0 ? (
              <p className="sm-muted">{t("no_data")}</p>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
